import styled from '@emotion/styled'
import { WorksheetDomain } from '@mathflat/domain/@entities'
import clsx from 'clsx'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import type { ReactNode } from 'react'
import { generatePath, useLocation } from 'react-router'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { useDailyWorksheetRemainingTime } from '~/@common/hooks/useDailyWorksheetRemainingTime'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { WORKSHEET_WRITE_KEY } from '~/@common/models/(Permission)/permissionUtils'
import { errorHandlerService } from '~/@common/services'
import { useLastLocationManager } from '~/@common/services/(LastLocationManager)'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { colors, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'
import { StudentLearningContentBackConfirmModal } from '~/@pages/student/@widgets/learningContent/StudentLearningContentBackConfirmModal'

import { StudentWorksheetDetailPageService } from '../../@service/StudentWorksheetDetailPage.service'

interface Props {
  title?: ReactNode
  enableSlotRight?: boolean
}

export const StudentWorksheetHeader = observer(({ title }: Props) => {
  const { studentWorksheetId } = useParams<{ studentWorksheetId: string }>()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const locationManager = useLastLocationManager()
  const { isMobile } = useStudentAppMediaQuery()

  const service = useRepository([StudentWorksheetDetailPageService, studentWorksheetId!])

  const { isDailyWorksheetAllowed } = commonRepo.permission
  const dailyWorksheetRemainingTime = useDailyWorksheetRemainingTime(isDailyWorksheetAllowed)

  const isScoringPage = pathname.includes('/scoring')
  // SlotRight 의 한문제씩/빠른채점 링크 노출 여부.
  // 기본 숨김, 학습지 write 권한이 명시적으로 허용된 경우만 노출.
  // 권한 미로드 / 학부모 등 모든 비-허용 케이스는 깜빡임 없이 hidden.
  const isWorksheetWriteEnabled = commonRepo.permission.byFeature.isAllowed(WORKSHEET_WRITE_KEY)
  const defaultPrevRouteName =
    service.worksheet?.type === WorksheetDomain.TYPE.오늘의학습
      ? getEntrancePage(authService.userType)
      : routeName.student.studentWorksheet

  const navigateBack = () => {
    if (!locationManager.prevState?.location.pathname) {
      navigate(defaultPrevRouteName)
      return
    }
    navigate(-1)
  }

  return (
    <PortalRootLayoutHeader>
      <GlobalHeader
        SlotCenter={
          service.worksheet?.type === WorksheetDomain.TYPE.오늘의학습 ? (
            <S.DailyTest>
              <div className={clsx(isMobile && 'mobile-title')}>{title}</div>
              {isDailyWorksheetAllowed && service.studentWorksheet?.status !== 'COMPLETE' && (
                <div className="remaining-time">
                  <span className="remaining-time-text">
                    {format(dailyWorksheetRemainingTime, 'HH:mm:ss')}
                  </span>
                  {!isMobile && <div>후 종료</div>}
                </div>
              )}
            </S.DailyTest>
          ) : (
            title
          )
        }
        SlotRight={
          isWorksheetWriteEnabled ? (
            isScoringPage ? (
              <Link
                to={generatePath(routeName.student.studentWorksheetById, { studentWorksheetId })}
              >
                <S.RightLink>
                  <Icon name="icon_description_paper" size={20} color={colors.gray.$900} />
                  <span>한문제씩</span>
                </S.RightLink>
              </Link>
            ) : (
              <Link
                to={generatePath(routeName.student.studentWorksheetScoringById, {
                  studentWorksheetId,
                })}
              >
                <S.RightLink>
                  <Icon name="icon_list" size={20} color={colors.gray.$900} />
                  <span>빠른채점</span>
                </S.RightLink>
              </Link>
            )
          ) : null
        }
        prevLinkProps={{
          to: defaultPrevRouteName,
          onClick: (e) => {
            e.preventDefault()

            // write 권한 차단(학부모 등) → 답안 제출 모달 자체 미노출, 즉시 뒤로
            if (!isWorksheetWriteEnabled || service.problemScoringViewOption?.채점불가능) {
              navigateBack()
              return
            }

            if (service.problemScoringColl?.toScoredArr.length) {
              modalService.openModal(
                <StudentLearningContentBackConfirmModal
                  defaultPrevRouteName={defaultPrevRouteName}
                  onSubmit={async () => {
                    if (!studentWorksheetId) {
                      return
                    }
                    try {
                      modalService.showLoader()
                      modalService.closeModal()

                      await service.onSubmitWorksheetProblems(studentWorksheetId)
                    } catch (error) {
                      errorHandlerService.handle(error)
                    } finally {
                      modalService.hideLoader()
                    }
                  }}
                />,
                {
                  modalName: '답안제출_안내',
                },
              )
              return
            }

            navigateBack()
          },
        }}
      />
    </PortalRootLayoutHeader>
  )
})

const S = {
  DailyTest: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;

    .mobile-title {
      ${typo.body02};
    }

    .remaining-time {
      display: flex;

      align-items: center;
      color: var(--Colors-Blue-600);

      gap: 4px;
      ${typo.caption01};
      font-weight: 700;

      .remaining-time-text {
        text-align: center;
        border-radius: var(--Radius-Circle);
        padding: 4px 10px;
        background: var(--Colors-Indigo-50);
      }
    }
  `,
  RightLink: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    ${typo.caption01};
    color: ${colors.gray.$900};
    font-weight: bold;

    cursor: pointer;

    ${mediaQuery.underDesktop} {
      ${typo.body02};
      font-weight: 400;
      color: ${colors.gray.$600};
      svg {
        color: ${colors.gray.$600};
      }
    }
  `,
}
